import { useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { FormInput, SubmitButton } from '@/components/form'
import { useTranslation } from 'react-i18next'

interface CustomOfferDetailsStepProps {
  desiredLicensesCount: string
  desiredBillingDetails: string
  onDesiredLicensesCountChange: (value: string) => void
  onDesiredBillingDetailsChange: (value: string) => void
  onNext: () => void
}

export function CustomOfferDetailsStep({
  desiredLicensesCount,
  desiredBillingDetails,
  onDesiredLicensesCountChange,
  onDesiredBillingDetailsChange,
  onNext,
}: CustomOfferDetailsStepProps) {
  const { t } = useTranslation('wizard')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const count = parseInt(desiredLicensesCount, 10)
    if (isNaN(count) || count < 1) {
      setError(t('customOfferForm.errors.invalidLicenses'))
      return
    }
    onNext()
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-2xl">
      <div className="mb-10 rounded-2xl border border-[#E9E9E9] bg-white p-6 shadow-[0px_-1px_4px_rgba(0,0,0,0.25)]">
        <h2 className="mb-4 text-base font-medium text-black">{t('customOfferForm.title')}</h2>

        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm font-normal text-red-600">{error}</p>
          </div>
        )}

        <FormInput
          id="desired-licenses"
          label={t('customOfferForm.desiredLicensesLabel')}
          required
          type="number"
          min={1}
          value={desiredLicensesCount}
          onChange={e => onDesiredLicensesCountChange(e.target.value)}
          placeholder={t('customOfferForm.desiredLicensesPlaceholder')}
          className="mb-4"
        />

        <div className="mb-6">
          <label
            htmlFor="desired-billing-details"
            className="mb-2 block text-sm font-semibold text-black"
          >
            {t('customOfferForm.detailsLabel')}
          </label>
          <textarea
            id="desired-billing-details"
            rows={4}
            value={desiredBillingDetails}
            onChange={e => onDesiredBillingDetailsChange(e.target.value)}
            placeholder={t('customOfferForm.detailsPlaceholder')}
            className="w-full rounded-lg border border-[#858C94] bg-white px-4 py-3.5"
          />
        </div>

        <SubmitButton>{t('finessVerification.nextButton')}</SubmitButton>
      </div>
    </form>
  )
}
